import { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import MultiSelectCheckboxDropdown from './MultiSelectCheckboxDropdown';
import DropdownButton from '../Common/DropDown';
import { toIdString, getSelectedProjectIds } from '../../utils/multiProjectUtils';

function MultiProjectFiltersRow({
  projectOptions,
  repoOptions,
  sprintOptions,
  releaseOptions,
  selectedProjects,
  selectedRepos,
  selectedSprintIds,
  selectedReleaseIds,
  filterMode,
  onProjectsChange,
  onBoardSelect,
  onReposChange,
  onSprintsChange,
  onReleasesChange,
  onFilterModeChange,
  getProjectBoards,
  loading,
  renderProjectOptionLabel,
}) {
  const navigate = useNavigate();
  const [isBoardOpen, setIsBoardOpen] = useState(false);
  const [subMenuBoards, setSubMenuBoards] = useState([]);
  const [subMenuPosition, setSubMenuPosition] = useState({ top: 0, left: 0 });
  const [currentProjectForBoard, setCurrentProjectForBoard] = useState(null);
  const [isValueOpen, setIsValueOpen] = useState(false);

  const selectedProjectIds = getSelectedProjectIds(selectedProjects);
  const projectsDisabled = !projectOptions.length;
  const scopeDisabled = !selectedProjectIds.length;

  const handleTypeChange = (option) => {
    onFilterModeChange(option.value);
  };

  const handleSprintsChange = (values) => {
    onSprintsChange(values);
  };

  const handleReleasesChange = (values) => {
    onReleasesChange(values);
  };

  const handleProjectHover = async (projectId) => {
    const boards = getProjectBoards?.(projectId) || [];
    setIsBoardOpen(false);
    setSubMenuBoards([]);
    setCurrentProjectForBoard(null);

    await new Promise((resolve) => setTimeout(resolve, 50));

    if (boards.length <= 1) {
      return;
    }

    const hoveredElement = document.querySelector(`[data-project-id="${projectId}"]`);
    if (hoveredElement) {
      const rect = hoveredElement.getBoundingClientRect();
      setSubMenuPosition({
        top: rect.top,
        left: rect.right + 10,
      });
    } else {
      setSubMenuPosition({ top: 100, left: 420 });
    }

    setSubMenuBoards(boards);
    setCurrentProjectForBoard(projectId);
    setIsBoardOpen(true);
  };

  const handleProjectMouseLeave = () => {
    setTimeout(() => {
      const submenuElement = document.querySelector('.board-submenu');
      const allProjectElements = document.querySelectorAll('[data-project-id]');
      let isHoveringOverProject = false;
      allProjectElements.forEach((element) => {
        if (element.matches(':hover')) {
          isHoveringOverProject = true;
        }
      });

      if (submenuElement && !submenuElement.matches(':hover') && !isHoveringOverProject) {
        setIsBoardOpen(false);
        setSubMenuBoards([]);
        setCurrentProjectForBoard(null);
      }
    }, 150);
  };

  const handleBoardSubmenuMouseLeave = () => {
    setTimeout(() => {
      const projectElement = document.querySelector(
        `[data-project-id="${currentProjectForBoard}"]`,
      );
      if (!projectElement || !projectElement.matches(':hover')) {
        setIsBoardOpen(false);
        setSubMenuBoards([]);
        setCurrentProjectForBoard(null);
      }
    }, 100);
  };

  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <button
        type="button"
        onClick={() => navigate('/standup')}
        className="px-3 py-1.5 text-sm rounded-md border border-[#066FD1] text-[#066FD1] hover:bg-[#066FD1]/10 transition-colors"
      >
        Back to Standup
      </button>

      <MultiSelectCheckboxDropdown
        title="Projects"
        placeholder="Select projects"
        options={projectOptions}
        selectedValues={selectedProjectIds}
        onSelect={onProjectsChange}
        onOptionHover={handleProjectHover}
        onOptionMouseLeave={handleProjectMouseLeave}
        optionDataAttribute
        width="lg"
        disabled={projectsDisabled || loading}
        showSelectAll
        renderOptionLabel={renderProjectOptionLabel}
      />

      {isBoardOpen && subMenuBoards.length > 0 && (
        <div
          className="board-submenu fixed z-[9999] bg-white dark:bg-[#182433] rounded-lg shadow-lg border border-gray-200 dark:border-[#30445A] min-w-[200px]"
          style={{
            top: `${subMenuPosition.top}px`,
            left: `${subMenuPosition.left}px`,
          }}
          onMouseLeave={handleBoardSubmenuMouseLeave}
        >
          <div className="py-2">
            {subMenuBoards.map((board, index) => {
              const currentProjectOption = projectOptions.find(p => p.value === currentProjectForBoard);
              const isSelected = currentProjectOption?.selectedBoardId === toIdString(board._id || board.id);

              return (
                <div
                  key={board._id || board.id || index}
                  className={`px-4 py-2 cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-blue-50 dark:bg-blue-900/30'
                      : 'hover:bg-gray-100 dark:hover:bg-[#1E2B3A]'
                  }`}
                  onClick={() => {
                    onBoardSelect(board._id || board.id, currentProjectForBoard);
                    setIsBoardOpen(false);
                    setSubMenuBoards([]);
                    setCurrentProjectForBoard(null);
                  }}
                >
                  <span className="text-sm text-gray-700 dark:text-[#D9E4F1]">
                    {board.boardName || board.name} ({board.boardType || board.type})
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <MultiSelectCheckboxDropdown
        title="Repositories"
        placeholder="Select repositories"
        options={repoOptions}
        selectedValues={selectedRepos}
        onSelect={onReposChange}
        width="lg"
        disabled={scopeDisabled || !repoOptions.length || loading}
        showSearch
      />

      <div className="mt-1">
        <DropdownButton
          options={[
            { value: 'sprint', label: 'Sprint' },
            { value: 'backlog', label: 'Backlog' },
            { value: 'release', label: 'Release' },
          ]}
          onSelect={handleTypeChange}
          selectedOption={
            filterMode === 'release'
              ? 'Release'
              : filterMode === 'backlog'
                ? 'Backlog'
                : 'Sprint'
          }
          isOpen={isValueOpen}
          setIsOpen={setIsValueOpen}
          width="sm"
          disabled={scopeDisabled || loading}
        />
      </div>

      {(filterMode === 'sprint' || !filterMode) && (
        <MultiSelectCheckboxDropdown
          title="Sprints"
          placeholder="Select sprints"
          options={sprintOptions.map((s) => ({
            value: toIdString(s._id),
            label: s.name,
            state: s.state,
          }))}
          selectedValues={selectedSprintIds}
          onSelect={handleSprintsChange}
          width="lg"
          disabled={scopeDisabled || !sprintOptions.length || loading}
          showSearch
        />
      )}

      {filterMode === 'release' && (
        <MultiSelectCheckboxDropdown
          title="Releases"
          placeholder="Select releases"
          options={releaseOptions.map((r) => ({
            value: toIdString(r._id),
            label: r.releaseName,
            status: r.status,
          }))}
          selectedValues={selectedReleaseIds}
          onSelect={handleReleasesChange}
          width="lg"
          disabled={scopeDisabled || !releaseOptions.length || loading}
          showSearch
        />
      )}
    </div>
  );
}

MultiProjectFiltersRow.propTypes = {
  projectOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  repoOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  sprintOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  releaseOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedProjects: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        projectId: PropTypes.string.isRequired,
        boardId: PropTypes.string,
      }),
    ]),
  ).isRequired,
  selectedRepos: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedSprintIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedReleaseIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  filterMode: PropTypes.string,
  onProjectsChange: PropTypes.func.isRequired,
  onBoardSelect: PropTypes.func.isRequired,
  onReposChange: PropTypes.func.isRequired,
  onSprintsChange: PropTypes.func.isRequired,
  onReleasesChange: PropTypes.func.isRequired,
  onFilterModeChange: PropTypes.func.isRequired,
  getProjectBoards: PropTypes.func,
  loading: PropTypes.bool,
  renderProjectOptionLabel: PropTypes.func,
};

export default MultiProjectFiltersRow;
